/**
 * ================================================================================================
 *  למי המשימה שייכת — the write shipped with no control, and the reason it had none
 * ================================================================================================
 *
 * `tenant.work_item.assign` has been built and tested since 0027 and `reachability.ts` carried it
 * as a write with no control, with this reason:
 *
 *     *"Assigning work is `tasks.item.manage` only — handing somebody else your task is not acting
 *     on your own — so a member picker drawn on the tasks screen for everybody would offer most
 *     people a control the server refuses. Since 0027 grants the tasks codes to ONE role per
 *     existing office, 'most people' is most people."*
 *
 * ── THE ANSWER IS NOT TO MOVE THE CONTROL, IT IS TO ASK THE SESSION ───────────────────────────
 *
 * The recorded plan was to put the picker on the administration surface, *"where the audience
 * already holds `tenant.user.manage`"*. That solves the wrong half: the audience would be right
 * and the CONTEXT would be wrong — a person assigning work needs the item in front of them, not a
 * member list with no task beside it.
 *
 * What makes the original objection correct is that a control the server refuses must not be
 * offered. The session already carries the caller's capability set, so this component renders
 * NOTHING for a principal without `tasks.item.manage` — the same set the gate expanded, read on
 * the client only to decide what to DRAW. It is never an authorization input: the server refuses
 * by name regardless, and `assign_needs_manage` is what a clerk who reaches the route anyway is
 * told.
 *
 * ── UNASSIGNING IS AN OPTION, BECAUSE THE SERVICE ACCEPTS ONE ─────────────────────────────────
 *
 * `assign` takes `membershipUid` as nullable and clears the assignment when it is absent. A picker
 * that could only hand work to somebody would make *"this is nobody's yet"* unreachable once
 * anybody had been named, which is a state a working office needs.
 */

import { useCallback, useState } from 'react';

import { ServerRead } from './ServerRead';
import { callOperation } from '../api/operations';
import type { WorkItemSummary } from '../api/contract';
import { useServerData } from '../api/useServerData';
import { useWorkContext } from '../shell/ContextProvider';

/** The capability the SERVICE requires. Quoted from `work-items.ts`'s own refusal, not paraphrased. */
const MANAGE = 'tasks.item.manage';

/** Nobody. The value the service reads as "clear the assignment". */
const UNASSIGNED = '';

export function AssigneePicker({
  item,
  sending,
  onAssign,
}: {
  readonly item: WorkItemSummary;
  readonly sending: boolean;
  /** `null` clears the assignment. The caller performs the write and owns the failure. */
  readonly onAssign: (membershipUid: string | null) => Promise<void>;
}): JSX.Element | null {
  const ctx = useWorkContext();

  /*
   * NOTHING AT ALL FOR A PRINCIPAL WHO MAY NOT ASSIGN. Not a disabled control and not an
   * explanation: a clerk looking at their own task has no question here, and a greyed-out picker
   * invites them to ask for a capability they do not need. The office's administrator sees it.
   *
   * THE GATE IS IN THE OUTER COMPONENT SO THE READ IS NEVER MADE. `useServerData` fetches on
   * mount and a hook cannot be called conditionally, so a single component would have asked for
   * the office's member list on behalf of every clerk who opened a task -- a read they may not
   * hold, answered with a refusal nobody rendered, once per row.
   */
  if (!(ctx.session?.capabilities ?? []).includes(MANAGE)) return null;

  return <AssigneeControl item={item} sending={sending} onAssign={onAssign} />;
}

function AssigneeControl({
  item,
  sending,
  onAssign,
}: {
  readonly item: WorkItemSummary;
  readonly sending: boolean;
  readonly onAssign: (membershipUid: string | null) => Promise<void>;
}): JSX.Element {
  const read = useCallback(() => callOperation('tenant.member.read', {}), []);
  const members = useServerData(read);
  const [choice, setChoice] = useState<string | null>(null);

  const current = item.assignedMembershipUid ?? UNASSIGNED;
  const selected = choice ?? current;

  return (
    <div className="field" data-area="assignee">
      <span className="field__label">אחראי</span>
      <ServerRead result={members.result} whatHe="את אנשי הארגון" skeletonRows={1}>
        {(data) => {
          /*
           * ONLY PEOPLE WHO CAN ACTUALLY RECEIVE IT. `membershipIdByUid` resolves an ACTIVE member
           * and the service answers `assignee_unknown` for anybody else — so offering a disabled
           * or departed colleague would be offering a choice the server rejects by name.
           */
          const assignable = data.members.filter((m) => m.status === 'active');
          return (
            <>
              <select
                className="field__input"
                name={`assignee-${item.uid}`}
                value={selected}
                disabled={sending}
                onChange={(e) => setChoice(e.currentTarget.value)}
              >
                {/* THE EMPTY OPTION IS A REAL CHOICE, not a placeholder. See the header. */}
                <option value={UNASSIGNED}>ללא אחראי</option>
                {assignable.map((m) => (
                  <option key={m.membershipUid} value={m.membershipUid}>
                    {m.displayName}
                  </option>
                ))}
              </select>

              {/*
                A SEPARATE PRESS, so changing the selection is not the write. A `select` whose
                `onChange` reassigns work means a mis-click hands somebody else's task away, and
                the row has no undo.
              */}
              <button
                type="button"
                className="btn"
                disabled={sending || selected === current}
                onClick={() => {
                  void onAssign(selected === UNASSIGNED ? null : selected);
                  setChoice(null);
                }}
              >
                {selected === UNASSIGNED ? 'הסרת האחראי' : 'עדכון האחראי'}
              </button>

              {/*
                WHAT IS IN FORCE, IN WORDS, beside a control that shows what is SELECTED. The two
                differ the moment somebody opens the list, and a row that showed only the selection
                would read as if the change had already been made.
              */}
              <span className="field__hint">
                {item.assignedDisplayName === null
                  ? 'כרגע אין אחראי למשימה הזאת.'
                  : `כרגע אחראי: ${item.assignedDisplayName}.`}
              </span>
            </>
          );
        }}
      </ServerRead>
    </div>
  );
}
